import { useState, useEffect } from "react";
import { ArrowRight, Loader2, User, Users, CheckCircle2, Info, Clock } from "lucide-react";
import { toast } from "sonner";
import { PageHero, SectionLabel } from "@/components/Section";
import { IMAGES, EVENT, CONTACT, PAYMENTS } from "@/data/content";
import { api } from "@/lib/api";
import { PaymentMethod } from "@/components/PaymentMethod";

const inputCls =
  "w-full border-b-2 border-border bg-transparent px-1 py-2.5 text-espresso rounded-none focus:outline-none focus:border-rust transition-colors placeholder:text-muted-foreground/60";

const PACKAGES = [
  { key: "team", label: "Four-Person Team", price: 1000, icon: Users, desc: "One foursome — the full scramble experience." },
  { key: "individual", label: "Individual Golfer", price: 250, icon: User, desc: "Single entry — we'll pair you into a team." },
];

const SHIRT_SIZES = ["XS", "S", "M", "L", "XL", "2XL", "3XL"];

export default function Register() {
  const [pkg, setPkg] = useState("team");
  const [capacity, setCapacity] = useState(null);
  useEffect(() => { api.get("/capacity").then((r) => setCapacity(r.data)).catch(() => {}); }, []);
  const [method, setMethod] = useState(PAYMENTS.registration ? "card" : "check");
  const payByCard = PAYMENTS.registration && method === "card";
  const [loading, setLoading] = useState(false);
  const [notice, setNotice] = useState(null);
  const [form, setForm] = useState({
    captain_name: "", email: "", phone: "", company: "", captain_shirt: "M",
    player2: "", player2_shirt: "M",
    player3: "", player3_shirt: "M",
    player4: "", player4_shirt: "M",
    notes: "",
  });

  const selected = PACKAGES.find((p) => p.key === pkg);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.captain_name || !form.email || !form.phone) {
      toast.error("Please fill in your name, email, and phone.");
      return;
    }

    if (!payByCard) {
      setLoading(true);
      try {
        const { data } = await api.post("/submit/registration", {
          package: pkg,
          captain_name: form.captain_name,
          email: form.email,
          phone: form.phone,
          company: form.company,
          captain_shirt: form.captain_shirt,
          player2: form.player2,
          player2_shirt: form.player2_shirt,
          player3: form.player3,
          player3_shirt: form.player3_shirt,
          player4: form.player4,
          player4_shirt: form.player4_shirt,
        });
        if (data.waitlisted) {
          setNotice({ type: "waitlist", message: data.message });
          toast.message("Added to the waitlist — see the note above.");
        } else {
          setNotice({ type: "success", message: data.message });
          toast.success(data.message || "Registration received!");
        }
        setForm({ captain_name: "", email: "", phone: "", company: "", captain_shirt: "M", player2: "", player2_shirt: "M", player3: "", player3_shirt: "M", player4: "", player4_shirt: "M", notes: "" });
        window.scrollTo({ top: 0, behavior: "smooth" });
      } catch (err) {
        toast.error("Something went wrong. Please try again.");
      } finally {
        setLoading(false);
      }
      return;
    }

    setLoading(true);
    try {
      const { data } = await api.post("/checkout/registration", {
        package: pkg,
        ...form,
        origin_url: window.location.origin,
      });
      if (data.waitlisted) {
        setNotice({ type: "waitlist", message: data.message });
        toast.message("Added to the waitlist — see the note above.");
        setForm({ captain_name: "", email: "", phone: "", company: "", captain_shirt: "M", player2: "", player2_shirt: "M", player3: "", player3_shirt: "M", player4: "", player4_shirt: "M", notes: "" });
        window.scrollTo({ top: 0, behavior: "smooth" });
        setLoading(false);
        return;
      }
      window.location.href = data.url;
    } catch (err) {
      setLoading(false);
      toast.error("Unable to start checkout. Please try again.");
    }
  };

  return (
    <div data-testid="register-page">
      <PageHero
        overline={`${EVENT.dateLabel} · ${EVENT.venue} — ${EVENT.venueCourse}`}
        title="Register"
        accent="A Team"
        subtitle={`Reserve your place in the inaugural Heroes & Legacy Golf Classic. Limited to ${EVENT.teams.toLowerCase()}.`}
        image={IMAGES.golferSwing}
      />

      <section className="paper-noise bg-background">
        <div className="max-w-5xl mx-auto px-5 sm:px-8 py-20 md:py-28 relative z-10">
          {notice && (
            <div
              data-testid={notice.type === "waitlist" ? "register-waitlist-notice" : "register-success-notice"}
              className={`mb-10 flex items-start gap-4 border p-6 hard-shadow-sm ${
                notice.type === "waitlist" ? "border-rust bg-rust/10" : "border-espresso bg-card"
              }`}
            >
              <span className={`shrink-0 h-10 w-10 flex items-center justify-center ${notice.type === "waitlist" ? "bg-rust text-primary-foreground" : "bg-espresso text-cream"}`}>
                {notice.type === "waitlist" ? <Info size={20} /> : <CheckCircle2 size={20} />}
              </span>
              <div>
                <div className="label-font text-xs text-rust">{notice.type === "waitlist" ? "Waitlist" : "Registration Received"}</div>
                <p className="mt-1 text-espresso leading-relaxed">{notice.message}</p>
              </div>
            </div>
          )}
          <SectionLabel>Choose Entry</SectionLabel>
          <h2 className="mt-5 font-serif font-black text-4xl text-espresso">Select Your Package</h2>
          {capacity && (
            <div data-testid="register-capacity" className={`mt-5 inline-flex items-center gap-2.5 label-font text-[11px] tracking-[0.12em] px-3.5 py-2 border ${capacity.sold_out ? "bg-espresso text-cream border-espresso" : capacity.players_remaining <= 16 ? "bg-rust text-primary-foreground border-rust" : "bg-rust/10 text-rust border-rust/40"}`}>
              <Clock size={13} />
              {capacity.sold_out
                ? "Sold out — join the waitlist"
                : `${capacity.players_remaining} of ${capacity.max_players} golfer spots remaining · ${Math.floor(capacity.teams_remaining)} team slots`}
            </div>
          )}

          <div className="mt-8 grid sm:grid-cols-2 gap-6">
            {PACKAGES.map((p) => (
              <button
                key={p.key}
                data-testid={`register-package-${p.key}`}
                onClick={() => setPkg(p.key)}
                className={`text-left border p-7 transition-transform hover:-translate-y-1 ${
                  pkg === p.key ? "border-rust bg-espresso text-cream hard-shadow-rust" : "border-border bg-card text-espresso hard-shadow-sm"
                }`}
              >
                <span className={`h-11 w-11 flex items-center justify-center ${pkg === p.key ? "bg-rust text-primary-foreground" : "bg-rust text-primary-foreground"}`}><p.icon size={20} /></span>
                <div className="mt-4 label-font text-xs text-rust">{p.label}</div>
                <div className="mt-1 font-serif font-black text-3xl">${p.price.toLocaleString()}</div>
                <p className={`mt-2 text-sm ${pkg === p.key ? "text-cream/80" : "text-muted-foreground"}`}>{p.desc}</p>
              </button>
            ))}
          </div>

          <form onSubmit={submit} className="mt-12 border border-border bg-card p-8 hard-shadow-sm">
            <h3 className="font-serif font-bold text-2xl text-espresso">Your Details</h3>
            <div className="mt-6 grid sm:grid-cols-2 gap-6">
              <div>
                <label className="label-font text-[11px] text-espresso/70">{pkg === "team" ? "Team Captain Name *" : "Full Name *"}</label>
                <input data-testid="register-captain" className={inputCls} value={form.captain_name} onChange={(e) => setForm({ ...form, captain_name: e.target.value })} placeholder="Full name" />
              </div>
              <div>
                <label className="label-font text-[11px] text-espresso/70">Company (optional)</label>
                <input data-testid="register-company" className={inputCls} value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} placeholder="Company name" />
              </div>
              <div>
                <label className="label-font text-[11px] text-espresso/70">Email *</label>
                <input data-testid="register-email" type="email" className={inputCls} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="you@email.com" />
              </div>
              <div>
                <label className="label-font text-[11px] text-espresso/70">Phone *</label>
                <input data-testid="register-phone" className={inputCls} value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="(713) 555-0100" />
              </div>
              <div>
                <label className="label-font text-[11px] text-espresso/70">{pkg === "team" ? "Captain Shirt Size" : "Shirt Size"}</label>
                <select data-testid="register-captain-shirt" className={inputCls} value={form.captain_shirt} onChange={(e) => setForm({ ...form, captain_shirt: e.target.value })}>
                  {SHIRT_SIZES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
            </div>

            {pkg === "team" && (
              <div className="mt-6">
                <div className="label-font text-[11px] text-espresso/70 mb-3">Additional Players & Shirt Sizes (optional)</div>
                <div className="space-y-4">
                  {[2, 3, 4].map((n) => (
                    <div key={n} className="grid grid-cols-3 gap-4">
                      <input
                        data-testid={`register-player${n}`}
                        className={`${inputCls} col-span-2`}
                        value={form[`player${n}`]}
                        onChange={(e) => setForm({ ...form, [`player${n}`]: e.target.value })}
                        placeholder={`Player ${n}`}
                      />
                      <select
                        data-testid={`register-player${n}-shirt`}
                        className={inputCls}
                        value={form[`player${n}_shirt`]}
                        onChange={(e) => setForm({ ...form, [`player${n}_shirt`]: e.target.value })}
                      >
                        {SHIRT_SIZES.map((s) => <option key={s} value={s}>{s}</option>)}
                      </select>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {PAYMENTS.registration && (
              <div className="mt-8">
                <PaymentMethod value={method} onChange={setMethod} prefix="register" />
              </div>
            )}

            <div className="mt-8 flex flex-col sm:flex-row items-center justify-between gap-4">
              <div className="text-espresso">
                <span className="label-font text-xs text-rust">Total</span>
                <div className="font-serif font-black text-3xl">${selected.price.toLocaleString()}</div>
              </div>
              <button
                data-testid="register-checkout-btn"
                type="submit"
                disabled={loading}
                className="label-font text-sm bg-rust text-primary-foreground px-8 py-4 border border-espresso flex items-center gap-2 transition-transform hover:-translate-y-0.5 disabled:opacity-60 hard-shadow-sm"
              >
                {loading ? <><Loader2 size={16} className="animate-spin" /> {payByCard ? "Redirecting…" : "Submitting…"}</> : payByCard ? <>Proceed to Payment <ArrowRight size={16} /></> : <>Submit Registration <ArrowRight size={16} /></>}
              </button>
            </div>
            <p className="mt-4 text-xs text-muted-foreground">
              {payByCard
                ? <>Secure checkout powered by Stripe. Questions? {CONTACT.registerEmail}</>
                : PAYMENTS.registration
                  ? <>Submit now to reserve your spot — we'll email check / invoice instructions to complete your registration.</>
                  : <>Online payments are coming soon. Submit now to reserve your spot — we'll follow up to confirm and arrange payment.</>}
            </p>
          </form>
        </div>
      </section>
    </div>
  );
}
